import type { IntlShape } from 'react-intl'
import type { FlightPartyPricingContext } from './buildFlightContext'

interface MultiCityFlightMessageArgs {
  intl: IntlShape
  from: string
  to: string
  selectedJourneyList: Array<App.JourneyV2>
  groupCurrency: string | undefined
  combinedTotal?: number
  farePricing?: FlightPartyPricingContext
  /** Renders the per-leg context block; each entry point keeps its own. */
  buildFlightContext: (journey: App.JourneyV2, fareFamily?: App.FlightFareFamily, pricing?: FlightPartyPricingContext) => string
}

/**
 * Builds the user-facing chat message and the assistant context for adding a
 * multi-city itinerary. Shared by the streamed-card search and the flight
 * search drawer so the grouping fields, combined price/currency, journey ids and
 * per-leg context never drift between the two entry points. Pure given its args
 * (price/currency are computed by the caller, since their data sources differ).
 */
export function buildMultiCityFlightMessage({
  intl,
  from,
  to,
  selectedJourneyList,
  groupCurrency,
  combinedTotal,
  farePricing,
  buildFlightContext,
}: MultiCityFlightMessageArgs): { userMessage: string; contextContent: string } {
  const userMessage = intl.formatMessage(
    {
      id: 'icHop6Aw2KYzb_NvSmi9',
      defaultMessage: 'Add multi-city flight from {from} to {to}',
      description: 'User chat message shown when adding a multi-city flight itinerary from the chat flight search results',
    },
    { from, to },
  )

  const legsContext = selectedJourneyList
    .map((journey, index) => `Flight ${index + 1}:\n${buildFlightContext(journey, undefined, farePricing)}`)
    .join('\n\n')

  // Tell the model to persist the canonical party-scoped total on every leg.
  // The exact compatible-itinerary total is never re-derived from per-leg fares.
  const contextContent = [
    `This is a single multi-city itinerary with ${selectedJourneyList.length} legs. Save each leg as a separate flight that shares one flightGroupId, sets flightGroupType=multi_city and an ordered legOrder starting at 1, preserves the Journey ID as journeyId${combinedTotal !== undefined && groupCurrency ? `, and sets fareTotalPrice=${combinedTotal} with fareCurrencyCode=${groupCurrency} on every leg.` : '. Do not invent or derive a saved party total when no exact compatible-itinerary total is provided.'}`,
    ...(combinedTotal !== undefined && groupCurrency ? [`Combined itinerary total: ${groupCurrency} ${combinedTotal}`] : []),
    legsContext,
  ].join('\n\n')

  return { userMessage, contextContent }
}
